import { useEffect, useState } from "react";
import { Form, Spinner, Table } from "react-bootstrap";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { Admin } from "../../types";
import searchIcon from "../../assets/icons/search.svg";
import toggleBtn from "../../assets/icons/toggle_btn.svg";
import FormInput from "../../components/FormInput";
import PaginationComponent from "../../components/PaginationComponent";
import TableColumnDropdownCheck from "../../components/TableColumnDropdownCheck";
import AdminDetailsModal from "./AdminDetailsModal";
import moment from "moment";

const AllAdmins = () => {
	const [admins, setAdmins] = useState<Admin[]>([]);
	const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
	const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);
	const [showModal, setShowModal] = useState(false);
	const [selectedType, setSelectedType] = useState('');
	const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({
		currentPage: 1,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
		nextPage: null,
		previousPage: null
	});

	const allColumns = [
		'Name', 'Email', 'Role', 'Type', 'Status'
	];

	const handlePaginate = (pageNumber: number) => {
		setPaginationData((prevData) => ({ ...prevData, currentPage: pageNumber }));
	};

	const handleViewAdminDetails = (admin: Admin) => {
		setSelectedAdmin(admin);
		setShowModal(true);
	};

	const capitalizeFirstLetter = (string: string) => {
		if (!string) return '';
		return string.charAt(0).toUpperCase() + string.slice(1);
	};

	const handleHideAdminDetails = () => {
		setShowModal(false);
		setSelectedAdminId(null);
	};

	const formatDate = (dateString: moment.MomentInput) => {
		return moment(dateString).format("DD-MM-YYYY");
	};

	const fetchAllAdmins = async () => {
		setIsLoading(true);

		try {
			api.setAuthorization(api.getLoggedInUser());
			const queryParams = new URLSearchParams({ 
				page: paginationData.currentPage?.toString() || '1' 
			});
			
			if (selectedType) {
				queryParams.set('type', selectedType);
			}

			const response = await api.get(endpoints.getAllAdmins, queryParams);
			
			if (response?.data?.data && Array.isArray(response.data.data)) {
				setAdmins(response.data.data);
				
				setPaginationData({
					currentPage: response.data.page || 1,
					totalPages: response.data.totalPages || 1,
					hasNextPage: response.data.hasNextPage || false,
					hasPreviousPage: response.data.hasPreviousPage || false,
					nextPage: response.data.nextPage,
					previousPage: response.data.previousPage
				});
			} else {
				console.error('Unexpected API response structure:', response);
			}

		} catch (error) {
			console.error("Error fetching admins:", error);
		} finally {
			setIsLoading(false);
		}
	};

		useEffect(() => {
		fetchAllAdmins();
	}, [paginationData.currentPage, selectedType]);


	return (
		<>
			<div className="table-topbar">
				<div className="search_bar">
					<img src={searchIcon} alt="search" />
					<FormInput name="search" placeholder="Search" className="search-bar-input" />
				</div>

				<div className="select-type">
					<Form.Select
						name="type"
						title="type"
						onChange={e => setSelectedType((e.target as HTMLSelectElement).value)}
						className="type-dropdown"
					>
						<option value="">Type</option>
						<option value="admin">Admin</option>
						<option value="superadmin">Super Admin</option>
					</Form.Select>
				</div>

				<div className="action-flex">
					<div className="column-select">
						<TableColumnDropdownCheck
							options={allColumns}
							selectedOptions={selectedColumns}
							onSelectionChange={setSelectedColumns}
						/>
					</div>
				</div>
			</div>

			<div className="table-body">
				<Table>
					{isLoading ? (
						<div className="spinner-div">
							<Spinner animation="border" size="sm" role="status" />
						</div>
					) : admins?.length > 0 ? (
						<>
							<thead>
								<tr>
									<th className="custom-thead-bg">
										<FormInput name="requery" type="checkbox" />
									</th>

									{selectedColumns.includes('Name') && <th className="custom-thead-bg">Name</th>}
									{selectedColumns.includes('Email') && <th className="custom-thead-bg">Email</th>}
									{selectedColumns.includes('Role') && <th className="custom-thead-bg">Role</th>}
									{selectedColumns.includes('Type') && <th className="custom-thead-bg">Type</th>}
									{selectedColumns.includes('Status') && <th className="custom-thead-bg">Status</th>}
									<th className="custom-thead-bg"></th>
								</tr>
							</thead>
							
							<tbody>
								{admins.map((admin) => (
									<tr key={admin.role_id}>
										<td className="custom-tbody">
											<FormInput name="requery" type="checkbox" />
										</td>

										{selectedColumns.includes('Name') && <td className="row-data">{`${admin.first_name} ${admin.last_name}`}</td>}
										{selectedColumns.includes('Email') && <td>{admin.email}</td>}
										{selectedColumns.includes('Role') && <td>{admin.role?.name || 'N/A'}</td>}
										{selectedColumns.includes('Type') && <td>{capitalizeFirstLetter(admin.user_type)}</td>}
										{/* {selectedColumns.includes('Status') &&
											<td>
												<div className={admin.disabled ? 'book-status-disabled' : 'book-status-enabled'}>
													{admin.disabled ? 'DISABLED' : 'ENABLED'}
												</div>
											</td>
										} */}
										<td>
											<div className="custom-popover">
												<img src={toggleBtn} alt="toggle open details" onClick={() => handleViewAdminDetails(admin)} />
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</>
					) : (
						<div className="no-results">
							No admins found.
						</div>
					)}
				</Table>
			</div>

			<PaginationComponent
				currentPage={paginationData.currentPage}
				totalPages={paginationData.totalPages}
				hasNextPage={paginationData.hasNextPage}
				hasPreviousPage={paginationData.hasPreviousPage}
				nextPage={paginationData.nextPage}
				previousPage={paginationData.previousPage}
				onPaginate={handlePaginate}
			/>

			{selectedAdmin && (
				<AdminDetailsModal
				  showModal={showModal}
					onHide={handleHideAdminDetails}
					adminId={selectedAdmin.id}
					formatDate={formatDate}
					capitalizeFirstLetter={capitalizeFirstLetter}
					details={selectedAdmin}
				/>
			)}
		</>
	)
}

export default AllAdmins;